// frontend/src/components/mobile/MobileQuickActions.jsx
// Project:      Vision Systems / Vision ANPR
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-09-23
// Updated:      2026-09-25 12:41 BST (settled top-position label fade)
//
// Description:  Theme-aware, rotatable mobile quick-action ring. Pointer capture
//               provides one gesture path for touch, pen, and mouse. The ring
//               snaps to an action after dragging. On touch/mobile interaction,
//               the label fades in only after the selected action settles at the
//               top position, hides immediately when movement starts, and fades
//               out before the selected action launches. Mouse hover and keyboard
//               focus labels remain available without showing labels while rotating.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import MobileQuickActionButton from './MobileQuickActionButton';

const DEFAULT_RADIUS = 142;
const MIN_RADIUS = 96;
const MAX_RADIUS = 156;
const DRAG_SENSITIVITY = 0.42;
const DRAG_THRESHOLD_PX = 6;
const INTERACTIVE_DEPTH_THRESHOLD = -0.02;
const LABEL_SETTLE_DELAY_MS = 140;
const LABEL_EXIT_DELAY_MS = 180;

function normaliseDegrees(value) {
  return ((value % 360) + 360) % 360;
}

function nearestStep(value, step) {
  return Math.round(value / step) * step;
}


function getTopActionId(actions, rotationAngle, angleStep) {
  if (!actions.length) return null;

  let topAction = actions[0];
  let smallestDistance = 360;

  actions.forEach((action, index) => {
    const currentAngle = normaliseDegrees(index * angleStep + rotationAngle);
    const distanceFromTop = Math.min(currentAngle, 360 - currentAngle);

    if (distanceFromTop < smallestDistance) {
      smallestDistance = distanceFromTop;
      topAction = action;
    }
  });

  return topAction?.id || null;
}

function calculateRadius(availableWidth, availableHeight) {
  if (!Number.isFinite(availableWidth) || availableWidth <= 0) {
    return DEFAULT_RADIUS;
  }

  const widthRadius = Math.floor((availableWidth - 72) / 2);
  const heightRadius = Number.isFinite(availableHeight)
    ? Math.floor((availableHeight - 112) / 2)
    : MAX_RADIUS;

  return Math.min(
    MAX_RADIUS,
    Math.max(MIN_RADIUS, Math.min(widthRadius, heightRadius)),
  );
}

export default function MobileQuickActions({
  actions = [],
  onAction,
  onRequestClose,
}) {
  const [rotationAngle, setRotationAngle] = useState(0);
  const [hoveredActionId, setHoveredActionId] = useState(null);
  const [settledActionId, setSettledActionId] = useState(null);
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const labelSettleTimeoutRef = useRef(null);
  const actionLaunchTimeoutRef = useRef(null);
  const gestureRef = useRef({
    active: false,
    didDrag: false,
    pointerId: null,
    startAngle: 0,
    startX: 0,
  });

  const itemCount = actions.length;
  const angleStep = itemCount > 0 ? 360 / itemCount : 360;

  useEffect(() => {
    const visualViewport = window.visualViewport;
    const updateRadius = () => {
      const viewportWidth = visualViewport?.width
        || document.documentElement.clientWidth;
      const viewportHeight = visualViewport?.height || window.innerHeight;

      setRadius(
        calculateRadius(Math.min(viewportWidth, 540), viewportHeight),
      );
    };

    updateRadius();
    window.addEventListener('resize', updateRadius);
    visualViewport?.addEventListener('resize', updateRadius);

    return () => {
      window.removeEventListener('resize', updateRadius);
      visualViewport?.removeEventListener('resize', updateRadius);
    };
  }, []);

  useEffect(() => {
    if (labelSettleTimeoutRef.current) {
      window.clearTimeout(labelSettleTimeoutRef.current);
      labelSettleTimeoutRef.current = null;
    }

    setHoveredActionId(null);
    setSettledActionId(null);
    setRotationAngle(0);

    if (actions[0]?.id) {
      labelSettleTimeoutRef.current = window.setTimeout(() => {
        setSettledActionId(actions[0].id);
        labelSettleTimeoutRef.current = null;
      }, LABEL_SETTLE_DELAY_MS);
    }

    return () => {
      if (labelSettleTimeoutRef.current) {
        window.clearTimeout(labelSettleTimeoutRef.current);
        labelSettleTimeoutRef.current = null;
      }
    };
  }, [actions]);

  useEffect(
    () => () => {
      if (labelSettleTimeoutRef.current) {
        window.clearTimeout(labelSettleTimeoutRef.current);
      }
      if (actionLaunchTimeoutRef.current) {
        window.clearTimeout(actionLaunchTimeoutRef.current);
      }
    },
    [],
  );

  const scheduleSettledLabel = useCallback(
    (snappedAngle) => {
      if (labelSettleTimeoutRef.current) {
        window.clearTimeout(labelSettleTimeoutRef.current);
      }

      setSettledActionId(null);

      const topActionId = getTopActionId(actions, snappedAngle, angleStep);
      if (!topActionId) return;

      labelSettleTimeoutRef.current = window.setTimeout(() => {
        setSettledActionId(topActionId);
        labelSettleTimeoutRef.current = null;
      }, LABEL_SETTLE_DELAY_MS);
    },
    [actions, angleStep],
  );

  const handlePointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    gestureRef.current = {
      active: true,
      didDrag: false,
      pointerId: event.pointerId,
      startAngle: rotationAngle,
      startX: event.clientX,
    };

    if (labelSettleTimeoutRef.current) {
      window.clearTimeout(labelSettleTimeoutRef.current);
      labelSettleTimeoutRef.current = null;
    }

    setSettledActionId(null);

    if (event.pointerType !== 'mouse') {
      setHoveredActionId(null);
    }

    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [rotationAngle]);

  const handlePointerMove = useCallback((event) => {
    const gesture = gestureRef.current;

    if (!gesture.active || gesture.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - gesture.startX;
    if (Math.abs(deltaX) >= DRAG_THRESHOLD_PX) {
      if (!gesture.didDrag) {
        setHoveredActionId(null);
      }
      gesture.didDrag = true;
    }

    setRotationAngle(gesture.startAngle + deltaX * DRAG_SENSITIVITY);
  }, []);

  const finishPointerGesture = useCallback((event) => {
    const gesture = gestureRef.current;

    if (!gesture.active || gesture.pointerId !== event.pointerId) {
      return;
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    gesture.active = false;

    if (event.pointerType !== 'mouse' || gesture.didDrag) {
      setHoveredActionId(null);
    }

    setRotationAngle((currentAngle) => {
      const snappedAngle = nearestStep(currentAngle, angleStep);
      scheduleSettledLabel(snappedAngle);
      return snappedAngle;
    });
  }, [angleStep, scheduleSettledLabel]);

  const handleActionSelect = useCallback(
    (action, event) => {
      if (gestureRef.current.didDrag) {
        event.preventDefault();
        gestureRef.current.didDrag = false;
        return;
      }

      if (labelSettleTimeoutRef.current) {
        window.clearTimeout(labelSettleTimeoutRef.current);
        labelSettleTimeoutRef.current = null;
      }
      if (actionLaunchTimeoutRef.current) {
        window.clearTimeout(actionLaunchTimeoutRef.current);
      }

      setHoveredActionId(null);
      setSettledActionId(null);

      actionLaunchTimeoutRef.current = window.setTimeout(() => {
        actionLaunchTimeoutRef.current = null;
        onAction(action.id, action);
        onRequestClose();
      }, LABEL_EXIT_DELAY_MS);
    },
    [onAction, onRequestClose],
  );

  const actionGeometry = useMemo(
    () =>
      actions.map((action, index) => {
        const baseAngle = index * angleStep;
        const currentAngle = normaliseDegrees(baseAngle + rotationAngle);
        const radians = (currentAngle - 90) * (Math.PI / 180);
        const x = radius * Math.cos(radians);
        const y = radius * Math.sin(radians);
        const angleFromApex = Math.min(currentAngle, 360 - currentAngle);
        const depthFactor = Math.cos(angleFromApex * (Math.PI / 180));
        const isInteractive = depthFactor > INTERACTIVE_DEPTH_THRESHOLD;

        let scale;
        let opacity;
        let zIndex;

        if (depthFactor > 0.4) {
          scale = 1.07 + depthFactor * 0.22;
          opacity = 1;
          zIndex = 30;
        } else if (isInteractive) {
          scale = 0.82 + depthFactor * 0.24;
          opacity = 0.5 + Math.max(depthFactor, 0) * 0.5;
          zIndex = 20;
        } else {
          scale = 0.62;
          opacity = 0;
          zIndex = 1;
        }

        return {
          action,
          isInteractive,
          style: {
            opacity,
            pointerEvents: isInteractive ? 'auto' : 'none',
            transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`,
            zIndex,
          },
        };
      }),
    [actions, angleStep, radius, rotationAngle],
  );

  if (itemCount === 0) {
    return null;
  }

  return (
    <section
      className="vision-mobile-quick-actions"
      role="group"
      aria-label="Quick actions"
      style={{
        '--vision-quick-action-diameter': `${radius * 2 + 88}px`,
      }}
      onPointerCancel={(event) => {
        setHoveredActionId(null);
        finishPointerGesture(event);
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishPointerGesture}
    >
      <div className="vision-mobile-quick-actions__orbit" aria-hidden="true" />

      {actionGeometry.map(
        ({ action, isInteractive, style }) => (
          <MobileQuickActionButton
            key={action.id}
            action={action}
            isHovered={
              hoveredActionId === action.id || settledActionId === action.id
            }
            isInteractive={isInteractive}
            onBlur={() => setHoveredActionId(null)}
            onFocus={() => setHoveredActionId(action.id)}
            onPointerEnter={(event) => {
              if (
                event.pointerType === 'mouse'
                && !gestureRef.current.active
              ) {
                setHoveredActionId(action.id);
              }
            }}
            onPointerLeave={() => setHoveredActionId(null)}
            onSelect={handleActionSelect}
            style={style}
          />
        ),
      )}
    </section>
  );
}

MobileQuickActions.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func,
        PropTypes.element,
        PropTypes.array,
      ]).isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  onAction: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};
